import axios from 'axios'
import moment from 'moment'
import { omitTrashedTrials } from './activeTrials'
import { normalizeTrialId } from './trialId'

export async function getTrials(patientID) {
    const token = localStorage.getItem("token");
    return fetch(window.SERVER_URL + `/a/get-trials-by-patient?patient_id=${patientID}`,{
        headers:{
            Authorization:'Bearer '+token
        }
    })
           .then(res => res.json())
           .then(omitTrashedTrials)
           .catch(err => console.log(err))
}
export async function getTrialInfo(trialID){
    const token = localStorage.getItem("token");
     return fetch(window.SERVER_URL + `/a/get-trial-by-id?trial_id=${trialID}`,{
        headers:{
            Authorization:'Bearer '+token
        }
     })
           .then(res => res.json())
           .catch(err => console.log(err))

}

/**
 * Fetch trial status and progress for polling (e.g. processing trials).
 * Backend: GET /a/get-trial-status?trial_id=<id> should return { result: { status, progress } }.
 * Returns { result: { status, progress, issue? } } or throws; safe to use in polling (catch per-id).
 */
export async function getTrialStatus(trialId) {
  const token = localStorage.getItem("token");
  const idParam = normalizeTrialId(trialId);
  if (idParam == null) return Promise.resolve(null);
  const url = `${window.SERVER_URL}/a/get-trial-status?trial_id=${encodeURIComponent(idParam)}`;
  const res = await fetch(url, {
    headers: { Authorization: "Bearer " + token },
  });
  if (!res.ok) throw new Error(`getTrialStatus HTTP ${res.status}`);
  const data = await res.json();
  return data;
}

/**
 * Batch trial status poll — one HTTP request (recordings page).
 * Backend: GET /a/get-trials-status?trial_ids=1,2,3
 * Returns { status, result: [{ id, status, progress, uploaded?, total?, issue?, status_display?, stage? }] }
 * `stage` (for example floor_detect) is the worker phase. It is not `issue`.
 * While status is `uploading`, `progress` is 0–100 from files the server has received.
 * `uploaded` / `total` are that same camera-file count; the recordings bar uses the greater of the two.
 */
export async function getTrialsStatusBatch(trialIds) {
  const token = localStorage.getItem("token");
  const ids = (Array.isArray(trialIds) ? trialIds : [])
    .map(normalizeTrialId)
    .filter(Boolean);
  if (ids.length === 0) return { status: 'success', result: [] };
  const url = `${window.SERVER_URL}/a/get-trials-status?trial_ids=${encodeURIComponent(ids.join(','))}`;
  const res = await fetch(url, {
    headers: { Authorization: "Bearer " + token },
  });
  if (!res.ok) throw new Error(`getTrialsStatusBatch HTTP ${res.status}`);
  return res.json();
}

/**
 * Poll status for the trials the UI is tracking. Uses one batch request; any ID
 * missing from the batch response is fetched individually so rows do not stall.
 */
export async function pollTrialsStatus(trialIds) {
  const ids = (Array.isArray(trialIds) ? trialIds : [])
    .map(normalizeTrialId)
    .filter(Boolean);
  if (ids.length === 0) return { status: 'success', result: [] };

  const batch = await getTrialsStatusBatch(ids);
  const results = Array.isArray(batch?.result) ? [...batch.result] : [];
  const returnedIds = new Set(results.map((row) => String(row.id)));
  const missingIds = ids.filter((id) => !returnedIds.has(String(id)));

  if (missingIds.length > 0) {
    const fallbacks = await Promise.all(
      missingIds.map(async (id) => {
        try {
          const res = await getTrialStatus(id);
          if (res?.result) {
            const row = { id, ...res.result };
            // Single-trial poll puts the phase on result.stage and trial.stage.
            if (row.stage == null && res.trial && res.trial.stage != null) {
              row.stage = res.trial.stage;
            }
            return row;
          }
        } catch (_) {
          /* per-id poll errors are non-fatal */
        }
        return null;
      })
    );
    results.push(...fallbacks.filter(Boolean));
  }

  return { ...batch, result: results };
}

function sessionHeaders() {
    const token = localStorage.getItem('token');
    return token ? { Authorization: 'Bearer ' + token } : {};
}

/**
 * Presigned URL for `{trial}_combined.mp4`. The browser video element cannot
 * send the session, so callers put this URL on `<source>`, not `/a/video`.
 */
export async function getTrialVideos(trialID) {
    return fetch(
        window.SERVER_URL + `/a/video?trial=${encodeURIComponent(trialID)}`,
        { headers: sessionHeaders() }
    )
        .then(async (res) => {
            if (!res.ok) throw new Error(`trial video HTTP ${res.status}`);
            const body = String(await res.text()).trim();
            if (!body) throw new Error('trial video URL empty');
            return body;
        })
        .catch(err => console.log(err))
}

export async function createTrial(sessionID, trialTags) {
    return fetch(window.SERVER_URL + '/api/record-trial', {
        method: 'POST',
        headers:  { 'Content-Type': 'application/json' },
        body: JSON.stringify({'session_name': sessionID, 'tags': trialTags})
    })
    .then(res => res.json())
    .catch(err => console.log(err))
}

export async function deleteTrial(id) {
    const token = localStorage.getItem("token");
    const idParam = normalizeTrialId(id);
    if (idParam == null) {
      console.warn("[deleteTrial] no valid id:", id);
      return;
    }
    try {
      const response = await axios.delete(window.SERVER_URL + '/a/delete-trial?id=' + encodeURIComponent(idParam), {
        headers: {
          Authorization: 'Bearer ' + token
        }
      });
      return response.data;
    } catch (err) {
      console.error("Error deleting trial:", err);
      throw err;
    }
  }

/** Move a single trial to trash (soft delete; purged after 30 days). */
export async function trashTrial(id) {
  const token = localStorage.getItem("token");
  const idParam = normalizeTrialId(id);
  if (idParam == null) {
    console.warn("[trashTrial] no valid id:", id);
    return;
  }
  try {
    const response = await axios.post(
      window.SERVER_URL + '/a/trash-trial',
      { id: idParam },
      { headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' } }
    );
    return response.data;
  } catch (err) {
    console.error("Error trashing trial:", err);
    throw err;
  }
}

/** Move multiple trials to trash. Falls back to hard delete if backend does not have trash endpoint (404). */
export async function trashTrials(ids) {
  const normalized = (Array.isArray(ids) ? ids : []).map(normalizeTrialId).filter(Boolean);
  if (normalized.length === 0) return { ok: true, trashed: 0 };
  try {
    const response = await axios.post(
      window.SERVER_URL + '/a/trash-trials',
      { ids: normalized },
      { headers: { Authorization: 'Bearer ' + localStorage.getItem("token"), 'Content-Type': 'application/json' } }
    );
    return response.data;
  } catch (err) {
    if (err?.response?.status === 404) {
      for (const id of normalized) {
        try {
          await deleteTrial(id);
        } catch (e) {
          console.error("Error deleting trial:", id, e);
        }
      }
      return { ok: true, trashed: normalized.length };
    }
    console.error("Error trashing trials:", err);
    throw err;
  }
}

// Note: restart-calibration is currently handled via /api/start-record on the
// recording API; there is no dedicated /a/restart-calibration flow in use
// from this frontend.

/**
 * IANA timezone at record time (browser). See docs/TRIAL_RECORDING_DATETIME_API.md.
 */
export function getRecordingTimezone() {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) return tz;
    } catch (_) {
        /* ignore */
    }
    console.warn('[updateTrialDate] Could not resolve recording timezone; using UTC');
    return 'UTC';
}

/** ISO-8601 with offset for POST /a/update-trial. */
export function formatTrialDateISO(date) {
    return moment(date).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
}

export async function updateTrialDate(trialID, date) {
    const token = localStorage.getItem("token");
    const dateStr = date instanceof Date ? formatTrialDateISO(date) : date;
    const recordedTimezone = getRecordingTimezone();
    const url =
        `${window.SERVER_URL}/a/update-trial?trial_id=${encodeURIComponent(trialID)}` +
        `&date=${encodeURIComponent(dateStr)}` +
        `&recorded_timezone=${encodeURIComponent(recordedTimezone)}`;
    return fetch(url, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token },
    })
        .then(res => res.json())
        .catch(err => {
            console.error('Error updating trial date:', err);
            throw err;
        });
}

/**
 * Reassign a trial to another patient (POST /a/update-trial, same query style as updateTrialDate).
 */
export async function updateTrialMetadata(trialID, patientId) {
  const token = localStorage.getItem("token");
  const idParam = normalizeTrialId(trialID);
  if (idParam == null) {
    console.warn("[updateTrialMetadata] no valid trial id:", trialID);
    return Promise.reject(new Error("Invalid trial id"));
  }
  if (patientId == null || String(patientId).length === 0) {
    return Promise.reject(new Error("Invalid patient id"));
  }
  const url =
    `${window.SERVER_URL}/a/update-trial?trial_id=${encodeURIComponent(idParam)}` +
    `&patient_id=${encodeURIComponent(String(patientId))}`;
  return fetch(url, {
    method: "POST",
    headers: { Authorization: "Bearer " + token },
  })
    .then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.reason || data.error || data.message || `Request failed (${res.status})`;
        throw new Error(msg);
      }
      return data;
    })
    .catch((err) => {
      console.error("Error updating trial patient:", err);
      throw err;
    });
}

/**
 * Device files already stored for this trial (`GET /a/check-evowalk-raw`).
 * The recorder downloads them off the EvoWalks when the trial stops.
 * Returns [] when the trial has none yet or the request fails.
 */
export async function listDownloadedDeviceFiles(trialID) {
    if (trialID == null || trialID === '') return [];
    const token = localStorage.getItem('token');
    const url =
        `${window.SERVER_URL}/a/check-evowalk-raw?id=${encodeURIComponent(trialID)}`;
    try {
        const res = await fetch(url, {
            headers: { Authorization: 'Bearer ' + token },
        });
        const data = await res.json();
        if (!data || data.status === 'failure' || !Array.isArray(data.files)) return [];
        return data.files;
    } catch (err) {
        console.error('Error listing downloaded device files:', err);
        return [];
    }
}

/**
 * Writes the Record page research tags onto an existing trial.
 *
 * Same route and auth convention as updateTrialDate; the fields go in a JSON
 * body because `comments` is free text and 17 query params is unworkable. The
 * backend treats this as a partial update, so only the tag columns are touched.
 */
export async function updateTrialTagFields(trialID, tagFields) {
    const token = localStorage.getItem("token");
    const url =
        `${window.SERVER_URL}/a/update-trial?trial_id=${encodeURIComponent(trialID)}`;
    return fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify(tagFields || {}),
    })
        .then(res => res.json())
        .catch(err => {
            console.error('Error updating trial tags:', err);
            throw err;
        });
}

export async function updateTrialTags(trialID, trialTags) {
    return fetch(window.SERVER_URL + '/api/update-trial-tags', {
        method: 'POST',
        headers:  { 'Content-Type': 'application/json' },
        body: JSON.stringify({'name': trialID, 'tags': trialTags})
    })
    .then(res => res.json())
    .catch(err => console.log(err))
}
export async function processTrial(trialID) {
    const token=localStorage.getItem('token')
    return axios.post(window.SERVER_URL + `/a/process-trial?trial_id=${trialID}`,{},{
        headers:{
            Authorization:'Bearer '+token
        }
    })
           .then(res => res.data)
           .catch(err => console.log(err))
}
export async function processCalib(calibID, boardType = '5x7') {
    const token=localStorage.getItem('token')
    return axios.post(window.SERVER_URL + `/a/process-calibration?calib_id=${calibID}&board_type=${boardType}`,{},{
        headers:{
            Authorization:'Bearer '+token
        }
    })
           .then(res => res.data)
           .catch(err => console.log(err))
}

export async function reprocessTrial(trialID) {
    const token = localStorage.getItem('token');
    return axios.post(window.SERVER_URL + `/a/reprocess-trial?trial_id=${trialID}`, {}, {
        headers: {
            Authorization: 'Bearer ' + token
        }
    })
        .then(res => res.data)
        .catch(err => console.log(err));
}
